import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Sparkles, ArrowRight, ArrowUp, PanelLeft, Flag, Loader2, SquarePen } from 'lucide-react';
import { ChatMessage, Meeting } from '../types';
import { plural, t, useLanguage } from '../lib/i18n';
import { MobileIconButton, MobileTitle } from '../features/mobile/MobileUI';
import { MobileMeetingsDrawer, type MobileChatHistory } from '../features/mobile/MobileMeetingsDrawer';
import { ChatThreadList } from '../features/chat/ChatThreadList';
import { ReportAiDialog, type AiReportTarget } from '../features/aiReport/ReportAiDialog';

interface AIChatViewProps {
  messages: ChatMessage[];
  onSendMessage: (text: string) => Promise<void>;
  meetings: Meeting[];
  onSelectMeeting: (meeting: Meeting) => void;
  isDarkMode: boolean;
  isResponding: boolean;
  /**
   * Telefon düzeni: büyük başlık, solda geçmiş toplantılar çekmecesi ve
   * kendi yazma çubuğu (masaüstündeki alt Omnibar telefonda yok).
   */
  isMobile?: boolean;
  /** Kayıtlı sohbetler (sunucuda şifreli geçmiş): masaüstünde sol panel, telefonda çekmecede sekme. */
  chats?: MobileChatHistory;
  /** Menüden seçilen sohbetin mesajları yükleniyor. */
  isOpening?: boolean;
}

export const AIChatView: React.FC<AIChatViewProps> = ({
  messages,
  onSendMessage,
  meetings,
  onSelectMeeting,
  isDarkMode,
  isResponding,
  isMobile = false,
  chats,
  isOpening = false
}) => {
  const { language } = useLanguage();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [draft, setDraft] = useState('');
  const [reportTarget, setReportTarget] = useState<AiReportTarget | null>(null);

  const submitDraft = (event: React.FormEvent) => {
    event.preventDefault();
    const text = draft.trim();
    if (!text || isResponding) return;
    setDraft('');
    void onSendMessage(text);
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isResponding]);

  /**
   * Öneriler kullanıcının GERÇEK toplantılarından türetiliyor.
   *
   * Önceki liste koda gömülüydü ve var olmayan kişilere/toplantılara atıfta
   * bulunuyordu ("Selin ve Emre hangi görevleri aldı?", "30 Ağustos
   * 'toplanti' görüşmesi"). Tıklandığında AI elbette "böyle bir toplantı
   * bulamadım" diyordu — yani ekrandaki tek yönlendirme, kullanıcıyı
   * doğrudan başarısız bir cevaba götürüyordu.
   */
  const quickPrompts = useMemo(() => {
    if (meetings.length === 0) {
      return [
        t('TalkNote ne yapabilir?', 'What can TalkNote do?'),
        t('Kayıt sırasında konuşmacılar nasıl ayrılıyor?', 'How are speakers separated during a recording?'),
      ];
    }

    const latest = meetings[0];
    const prompts = [
      t(`"${latest.title}" toplantısının özetini çıkar`, `Summarize the meeting "${latest.title}"`),
      t('Açık kalan tüm aksiyon maddelerini listele', 'List all my open action items'),
    ];

    if (meetings.length > 1) {
      prompts.push(t('Bu haftaki toplantılarda alınan kararları topla', 'Collect the decisions made in this week’s meetings'));
    }

    const withPeople = meetings.find((m) => m.participants.length > 0);
    if (withPeople) {
      const name = withPeople.participants[0].name;
      prompts.push(t(`${name} ile neler konuştuk?`, `What did we discuss with ${name}?`));
    }

    return prompts.slice(0, 4);
    // Öneriler seçili dilde üretiliyor.
  }, [meetings, language]);

  return (
    <div className={`h-full flex select-none ${
      isDarkMode ? 'bg-[#111113] text-neutral-200' : 'bg-neutral-50 text-neutral-900'
    }`}>
      {!isMobile && chats && (
        <aside className="w-[250px] shrink-0 border-r border-border pt-5 pb-3 px-2 flex flex-col">
          <ChatThreadList
            threads={chats.threads}
            activeId={chats.activeId}
            onNew={chats.onNew}
            onOpen={chats.onOpen}
            onRename={chats.onRename}
            onDelete={chats.onDelete}
            onDeleteAll={chats.onDeleteAll}
          />
        </aside>
      )}
      <div className="flex-1 min-w-0 h-full flex flex-col">
      {isMobile && (
        <div className="px-5 pb-2 shrink-0">
          <MobileTitle
            title={t('Sohbet', 'Chat')}
            leading={
              <MobileIconButton
                label={chats ? t('Sohbetler ve toplantılar', 'Chats and meetings') : t('Geçmiş toplantılar', 'Past meetings')}
                onClick={() => setIsDrawerOpen(true)}
              >
                <PanelLeft className="w-[22px] h-[22px]" strokeWidth={1.8} />
              </MobileIconButton>
            }
            trailing={
              chats && messages.length > 0 ? (
                <MobileIconButton label={t('Yeni sohbet', 'New chat')} onClick={chats.onNew}>
                  <SquarePen className="w-[20px] h-[20px]" strokeWidth={1.8} />
                </MobileIconButton>
              ) : undefined
            }
          />
        </div>
      )}

      {/* Chat Header — sınırsız, sayfa arkaplanıyla aynı ton */}
      <div className={`px-8 py-5 items-center justify-between shrink-0 ${isMobile ? 'hidden' : 'flex'}`}>
        <div>
          <h1 className={`text-[15px] font-semibold tracking-tight ${isDarkMode ? 'text-[#F0EEE6]' : 'text-neutral-900'}`}>
            {t('TalkNote AI Asistanı', 'TalkNote AI assistant')}
          </h1>
          <p className="text-[12px] text-neutral-500 mt-0.5">
            {t(
              'Claude ile çalışır — aşağıdaki çubuğa yazın; sorunuza göre yalnızca sizin toplantılarınızı, özetlerini ve transkriptlerini okur',
              'Powered by Claude — type in the bar below; it reads only your own meetings, their summaries and transcripts as needed'
            )}
          </p>
        </div>

        <div className="flex items-center gap-1.5 text-[11px] text-neutral-500">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500/70"></span>
          <span>{t(`${meetings.length} toplantı bağlamda`, `${plural(meetings.length, 'meeting', 'meetings')} in context`)}</span>
        </div>
      </div>

      {/* Messages Scroll Area */}
      <div className={`flex-1 overflow-y-auto pb-6 space-y-5 max-w-3xl mx-auto w-full no-scrollbar ${isMobile ? 'px-5' : 'px-8'}`}>
        {isOpening && (
          <div className="h-full flex items-center justify-center text-neutral-500">
            <Loader2 className="w-5 h-5 animate-spin" />
          </div>
        )}

        {messages.length === 0 && !isResponding && !isOpening && (
          <div className="h-full flex flex-col items-center justify-center text-center gap-3.5 -mt-8">
            <span className={`w-11 h-11 rounded-2xl flex items-center justify-center ${
              isDarkMode ? 'bg-[#19191C] text-neutral-500' : 'bg-neutral-200 text-neutral-500'
            }`}>
              <Sparkles className="w-5 h-5" />
            </span>
            <p className={`text-neutral-500 max-w-xs leading-relaxed ${isMobile ? 'text-[15px]' : 'text-[13.5px]'}`}>
              {isMobile
                ? t('Kararları, aksiyonları ya da kimin ne dediğini toplantılarına sor.', 'Ask your meetings about decisions, action items or who said what.')
                : meetings.length > 0
                ? t(
                    'Toplantılarınız hakkında soru sorun. Aşağıdaki önerilerden birine tıklayabilir ya da alttaki çubuğa yazabilirsiniz.',
                    'Ask about your meetings. Pick one of the suggestions below or type in the bar at the bottom.'
                  )
                : t(
                    'Henüz kayıtlı toplantınız yok. Bir kayıt aldıktan sonra buradan içeriği hakkında soru sorabilirsiniz.',
                    'You have no recorded meetings yet. Once you record one, you can ask about it here.'
                  )}
            </p>
          </div>
        )}

        {messages.map((msg, index) => {
          const isUser = msg.sender === 'user';

          if (isUser) {
            return (
              <div key={msg.id} className="flex flex-col items-end">
                <div className={`max-w-[75%] text-sm font-medium leading-relaxed text-right ${
                  isDarkMode ? 'text-neutral-100' : 'text-neutral-900'
                }`}>
                  {msg.text}
                </div>
                <div className="text-[11px] text-neutral-600 mt-1 font-mono">{msg.timestamp}</div>
              </div>
            );
          }

          return (
            <div key={msg.id} className="flex items-start gap-3">
              <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 mt-0.5 ${
                isDarkMode ? 'bg-[#19191C] text-neutral-400' : 'bg-neutral-200 text-neutral-500'
              }`}>
                <Sparkles className="w-3.5 h-3.5" />
              </div>

              <div className={`max-w-[85%] rounded-2xl border p-4 text-sm leading-relaxed ${
                isDarkMode ? 'bg-[#19191C] border-[#3A3A3E] text-neutral-200' : 'bg-white border-neutral-200 text-neutral-800'
              }`}>
                <div className="whitespace-pre-line">{msg.text}</div>

                {msg.referencedMeetingId && (
                  <div className={`mt-3 pt-2.5 border-t flex items-center justify-between ${
                    isDarkMode ? 'border-[#3A3A3E]' : 'border-neutral-200'
                  }`}>
                    <span className="text-[11px] text-neutral-500">{t('İlgili Toplantı:', 'Related meeting:')}</span>
                    <button
                      onClick={() => {
                        const m = meetings.find(item => item.id === msg.referencedMeetingId);
                        if (m) onSelectMeeting(m);
                      }}
                      className="text-[11px] font-semibold text-[#46CBE2] hover:text-[#7BDAEA] flex items-center gap-1"
                    >
                      <span>{msg.referencedMeetingTitle}</span>
                      <ArrowRight className="w-3 h-3" />
                    </button>
                  </div>
                )}

                <div className="mt-1.5 flex items-center justify-between gap-3">
                  <span className="text-[10px] text-neutral-500 font-mono">{msg.timestamp}</span>
                  {/* Yapay zekâ yanıtını bildir (Google Play yapay zekâ içeriği politikası). */}
                  {!msg.isError && (
                    <button
                      onClick={() =>
                        setReportTarget({
                          surface: 'chat',
                          content: msg.text,
                          prompt: messages.slice(0, index).reverse().find((item) => item.sender === 'user')?.text,
                        })
                      }
                      title={t('Yanıtı bildir', 'Report response')}
                      aria-label={t('Yanıtı bildir', 'Report response')}
                      className={`flex items-center gap-1 rounded-md text-neutral-500 hover:text-rose-400 transition-colors ${
                        isMobile ? 'text-[12px] px-1.5 py-1 -my-1' : 'text-[11px] px-1 py-0.5'
                      }`}
                    >
                      <Flag className="w-3 h-3" />
                      {t('Bildir', 'Report')}
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}

        {isResponding && (
          <div className="flex items-center gap-3">
            <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${
              isDarkMode ? 'bg-[#19191C] text-neutral-400' : 'bg-neutral-200 text-neutral-500'
            }`}>
              <Sparkles className="w-3.5 h-3.5" />
            </div>
            <div className={`px-4 py-2.5 rounded-2xl border text-sm flex items-center gap-1.5 ${
              isDarkMode ? 'bg-[#19191C] border-[#3A3A3E] text-neutral-400' : 'bg-white border-neutral-200 text-neutral-500'
            }`}>
              <span className="w-1.5 h-1.5 rounded-full bg-neutral-500 animate-bounce"></span>
              <span className="w-1.5 h-1.5 rounded-full bg-neutral-500 animate-bounce [animation-delay:0.2s]"></span>
              <span className="w-1.5 h-1.5 rounded-full bg-neutral-500 animate-bounce [animation-delay:0.4s]"></span>
              <span className="ml-1 text-[12px]">{t('Yanıt yazılıyor...', 'Writing a reply…')}</span>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      {/* Suggested Quick Prompts — gönderim tek bir yerden, alttaki Omnibar'dan yapılıyor */}
      <div className={`max-w-3xl mx-auto w-full shrink-0 ${isMobile ? 'px-5 pb-3' : 'px-8 pb-6'}`}>
        <div className="flex items-center gap-2 overflow-x-auto no-scrollbar py-1">
          {quickPrompts.map((prompt, idx) => (
            <button
              key={idx}
              disabled={isResponding}
              onClick={() => onSendMessage(prompt)}
              className={`px-3.5 rounded-full border whitespace-nowrap transition-colors disabled:opacity-40 ${
                isMobile ? 'h-10 text-[14px]' : 'py-1.5 text-[12px]'
              } ${
                isDarkMode
                  ? 'bg-[#19191C] border-[#3A3A3E] text-neutral-400 hover:text-neutral-100 hover:border-[#48484F]'
                  : 'bg-white border-neutral-200 text-neutral-600 hover:border-neutral-400'
              }`}
            >
              {prompt}
            </button>
          ))}
        </div>
        <p className="mt-2 text-[11px] leading-snug text-neutral-500">
          {t(
            'Yanıtlar yapay zekâ tarafından üretilir ve hatalı olabilir. Önemli bilgileri kayıtla karşılaştır.',
            'Replies are AI-generated and may be wrong. Check important details against the recording.'
          )}
        </p>
      </div>

      {isMobile && (
        <form onSubmit={submitDraft} className="shrink-0 mx-3 mb-2 flex items-center gap-2 pl-4 pr-1.5 py-1.5 rounded-[26px] border border-border bg-card">
          <input
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            placeholder={t('Bir soru yaz…', 'Ask a question…')}
            aria-label={t('Soru', 'Question')}
            enterKeyHint="send"
            className="flex-1 min-w-0 h-10 bg-transparent border-0 outline-none text-[16px] text-foreground placeholder:text-muted-foreground"
          />
          <button
            type="submit"
            disabled={!draft.trim() || isResponding}
            aria-label={t('Gönder', 'Send')}
            className="w-10 h-10 rounded-full bg-brand text-on-brand flex items-center justify-center shrink-0 disabled:opacity-40"
          >
            <ArrowUp className="w-5 h-5" strokeWidth={2.2} />
          </button>
        </form>
      )}

      <ReportAiDialog target={reportTarget} onClose={() => setReportTarget(null)} />

      {isMobile && (
        <MobileMeetingsDrawer
          open={isDrawerOpen}
          onClose={() => setIsDrawerOpen(false)}
          meetings={meetings}
          onSelectMeeting={onSelectMeeting}
          chats={chats}
        />
      )}
      </div>
    </div>
  );
};
